import {
  isPosAllowed,
  isGoFatafatEnabled,
  isPathBlockedForCompany,
  isSectionAllowedForCompany,
} from "./companyCapabilities";

const getRolePermissions = (user) => {
  return (
    user?.userData?.userRoles ||
    user?.userData?.UserRoles ||
    user?.userRoles ||
    user?.UserRoles ||
    null
  );
};

const getBoolean = (obj, camelKey, pascalKey) => {
  if (!obj) return false;
  if (typeof obj[camelKey] === "boolean") return obj[camelKey];
  if (typeof obj[pascalKey] === "boolean") return obj[pascalKey];
  // API sometimes returns 1/0 or "true"/"false"
  const raw = obj[camelKey] ?? obj[pascalKey];
  if (raw === 1 || raw === "1" || raw === "true" || raw === "True") return true;
  if (raw === 0 || raw === "0" || raw === "false" || raw === "False") return false;
  return false;
};

export const isAdminUser = (user) => {
  const role = getRolePermissions(user);
  return (
    getBoolean(role, "isAdmin", "IsAdmin") ||
    getBoolean(user?.userData, "isAdmin", "IsAdmin")
  );
};

export const hasSectionAccess = (user, sectionKey) => {
  if (!user) return false;

  // Company capability limits apply to everyone (including admins) so sidebar
  // links never point at routes PrivateRoute will bounce back to dashboard.
  const profileData = user?.profileData;
  if (!isSectionAllowedForCompany(profileData, sectionKey)) return false;

  if (isAdminUser(user)) return true;

  const role = getRolePermissions(user);
  if (!role) return false;

  switch (sectionKey) {
    case "dashboard":
      return getBoolean(role, "canViewDashboard", "CanViewDashboard");
    case "orders":
      return getBoolean(role, "canAccessOrders", "CanAccessOrders");
    case "reports":
      return getBoolean(role, "canAccessReports", "CanAccessReports");
    case "staff":
      return getBoolean(role, "canAccessStaff", "CanAccessStaff");
    case "customers":
      return getBoolean(role, "canAccessCustomers", "CanAccessCustomers");
    case "itemCatalog":
      return getBoolean(role, "canAccessItemCatalog", "CanAccessItemCatalog");
    case "purchase":
      return getBoolean(role, "canAccessPurchase", "CanAccessPurchase");
    case "diningManagement":
      return getBoolean(role, "canAccessDiningManagement", "CanAccessDiningManagement");
    case "settings":
      return getBoolean(role, "canAccessSettings", "CanAccessSettings");
    case "administration":
      return getBoolean(role, "canAccessAdministration", "CanAccessAdministration");
    default:
      return true;
  }
};

/** Sale reports allowed for marketplace / online-only companies (role: IsAllowedSaleReport). */
const marketplaceSaleReportPaths = [
  "/report/salesbreakdownbytime",
  "/report/salesbyproducts",
  "/report/dealperformance",
  "/report/restaurantperformance",
];

/** Sale reports that still require POS (company + role). */
const posOnlySaleReportPaths = [
  "/report/zreport",
  "/report/tablespent",
];

const generalReportPaths = [
  "/report/marketplacesettlementreport",
  "/report/marketplaceorderearningsreport",
];

/** POS-oriented reports that still use the general-report role flag when POS is on. */
const posGeneralReportPaths = [
  "/report/customerreport",
  "/report/riderreport",
  "/report/reconciliationreport",
  "/report/purchasesupplierreport",
  "/report/requisitionreport",
  "/report/masterinventoryreport",
  "/report/staffactivitylogreport",
  "/report/pettyfloatcashreport",
];

const roleAllowsSaleReport = (user) => {
  if (isAdminUser(user)) return true;
  const role = getRolePermissions(user);
  return getBoolean(role, "isAllowedSaleReport", "IsAllowedSaleReport");
};

export const hasReportRouteAccess = (user, pathname) => {
  if (!hasSectionAccess(user, "reports")) return false;

  const path = (pathname || "").toLowerCase();
  // Company capability gate (online-only cannot open POS-only reports)
  if (isPathBlockedForCompany(user?.profileData, path)) return false;

  if (isAdminUser(user)) return true;

  const role = getRolePermissions(user);

  if (marketplaceSaleReportPaths.some((prefix) => path.startsWith(prefix))) {
    return getBoolean(role, "isAllowedSaleReport", "IsAllowedSaleReport");
  }

  if (posOnlySaleReportPaths.some((prefix) => path.startsWith(prefix))) {
    if (!isPosAllowed(user?.profileData)) return false;
    return getBoolean(role, "isAllowedSaleReport", "IsAllowedSaleReport");
  }

  if (generalReportPaths.some((prefix) => path.startsWith(prefix))) {
    return getBoolean(role, "isAllowedGeneralReport", "IsAllowedGeneralReport");
  }

  if (posGeneralReportPaths.some((prefix) => path.startsWith(prefix))) {
    if (!isPosAllowed(user?.profileData)) return false;
    return getBoolean(role, "isAllowedGeneralReport", "IsAllowedGeneralReport");
  }

  return true;
};

export const resolveSectionByPath = (pathname) => {
  const path = (pathname || "").toLowerCase();

  if (path === "/" || path.startsWith("/dashboard")) return "dashboard";
  if (path.startsWith("/order")) return "orders";
  if (path.startsWith("/report")) return "reports";
  if (path.startsWith("/team") || path.startsWith("/role")) return "staff";
  if (path.startsWith("/customer") || path.startsWith("/customergroup")) return "customers";
  if (
    path.startsWith("/items") ||
    path.startsWith("/categories") ||
    path.startsWith("/add-ons") ||
    path.startsWith("/food-menu") ||
    path.startsWith("/categorytypes") ||
    path.startsWith("/departments") ||
    path.startsWith("/discount") ||
    path.startsWith("/voucher") ||
    path.startsWith("/recipes") ||
    path.startsWith("/batches") ||
    path.startsWith("/media")
  ) {
    return "itemCatalog";
  }
  if (path.startsWith("/purchaseorder") || path.startsWith("/supplier") || path.startsWith("/requisition")) return "purchase";
  if (path.startsWith("/diningmanagement") || path.startsWith("/diningareas") || path.startsWith("/servicearea")) return "diningManagement";
  if (
    path.startsWith("/setting") ||
    path.startsWith("/managedevices") ||
    path.startsWith("/printingreceipts") ||
    path.startsWith("/paymentmethod")
  ) {
    return "settings";
  }
  if (
    path.startsWith("/profile") ||
    path.startsWith("/pettycash") ||
    path.startsWith("/branches") ||
    path.startsWith("/administration/billing")
  ) {
    return "administration";
  }

  return null;
};

export const getDefaultAuthorizedPath = (user) => {
  const profileData = user?.profileData;
  const posAllowed = isPosAllowed(profileData);

  // Go Fatafat-only (no POS): land on marketplace settings, not the POS dashboard.
  if (!posAllowed && isGoFatafatEnabled(profileData)) {
    return "/gofatafat/settings";
  }

  const orderedSections = [
    { key: "dashboard", path: "/dashboard" },
    { key: "orders", path: "/Order/manageOrder" },
    {
      key: "reports",
      path: posAllowed
        ? "/report/customerReport"
        : "/report/restaurantPerformance",
    },
    { key: "staff", path: "/team/manageTeam" },
    { key: "customers", path: "/customer/manageCustomer" },
    { key: "itemCatalog", path: "/items/manageItems" },
    { key: "purchase", path: "/purchaseOrder/managePurchaseOrders" },
    { key: "diningManagement", path: "/diningAreas" },
    { key: "settings", path: "/setting" },
    { key: "administration", path: "/profile" },
  ];

  const firstAllowed = orderedSections.find(
    (section) =>
      hasSectionAccess(user, section.key) &&
      !isPathBlockedForCompany(profileData, section.path)
  );
  // Never send an authenticated user back to login — that clears the UX into a bounce loop.
  return firstAllowed ? firstAllowed.path : "/profile";
};

export const getReportPermissions = (user) => {
  const posAllowed = isPosAllowed(user?.profileData);
  const canAccessReports = hasSectionAccess(user, "reports");
  const saleRoleAllowed = canAccessReports && roleAllowsSaleReport(user);

  if (isAdminUser(user)) {
    return {
      canAccessReports,
      // Core sales reports (breakdown / by product) — allowed for marketplace too
      isAllowedMarketplaceSaleReport: canAccessReports,
      // Full POS sale reports (Z, deals, table spent) — POS companies only
      isAllowedSaleReport: canAccessReports && posAllowed,
      isAllowedGeneralReport: canAccessReports,
    };
  }

  const role = getRolePermissions(user);
  return {
    canAccessReports,
    isAllowedMarketplaceSaleReport: saleRoleAllowed,
    isAllowedSaleReport: saleRoleAllowed && posAllowed,
    isAllowedGeneralReport:
      canAccessReports &&
      getBoolean(role, "isAllowedGeneralReport", "IsAllowedGeneralReport"),
  };
};
